import { type ComponentType } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu } from 'lucide-react'
import { useSidebar } from '../ui/sidebar'
import { cn } from '../../lib/utils'

export type MobileTab = {
  key: string
  href: string
  icon: ComponentType<{ className?: string }>
  /** i18n key + English fallback for the label under the icon. */
  labelKey: string
  fallback: string
}

/**
 * Phone-only bottom navigation: the app's few everyday destinations one thumb
 * away, plus "More", which opens the full menu drawer for everything else.
 * AppShell renders it below the content panel only when `useIsMobile()` is
 * true, so tablets and desktops keep the sidebar exactly as before.
 *
 * Tap targets are the full tab cell (≥ 56px tall), and the bar pads itself by
 * the home-indicator safe area so it is never under the iPhone gesture bar.
 */
export default function MobileTabBar({ tabs }: { tabs: MobileTab[] }) {
  const { t } = useTranslation()
  const { setOpenMobile } = useSidebar()
  const cell = 'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 text-[11px] font-medium transition-colors'
  return (
    <nav
      aria-label={t('nav.primary', 'Main')}
      className="shrink-0 rounded-2xl border border-sidebar-border bg-sidebar shadow pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-stretch gap-1 p-1">
        {tabs.map(tab => (
          <NavLink
            key={tab.key}
            to={tab.href}
            className={({ isActive }) => cn(
              cell,
              isActive
                ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                : 'text-sidebar-foreground/70 active:bg-sidebar-accent',
            )}
          >
            <tab.icon className="h-5 w-5" />
            <span className="max-w-full truncate">{t(tab.labelKey, tab.fallback)}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setOpenMobile(true)}
          className={cn(cell, 'text-sidebar-foreground/70 active:bg-sidebar-accent')}
        >
          <Menu className="h-5 w-5" />
          <span>{t('common.more', 'More')}</span>
        </button>
      </div>
    </nav>
  )
}
